import { reports } from "../api/client";
import Globe from "../components/Globe";
import HouseIcon from "../components/HouseIcon";
import Logo from "../components/Logo";
import { fmtEUR } from "../lib/auth";
import { useGoalTree, useHousehold, useWidgetData } from "./data";
import type { WidgetComponentProps } from "./registry";

const today = new Date();

export default function BoatHeroWidget(_props: WidgetComponentProps) {
  const tree = useGoalTree();
  const hh = useHousehold();
  // Month in the key: the cache persists across app restarts, so a fixed key
  // would flash last month's numbers after a month rollover.
  const monthly = useWidgetData(
    `goalMonthly:${today.getFullYear()}-${today.getMonth() + 1}`,
    () => reports.goalMonthly(today.getFullYear(), today.getMonth() + 1),
  );
  const node = tree?.[0];
  if (!node) return null;
  const theme = hh?.goal_theme ?? "boat";
  const monthlyContrib = monthly?.total ?? 0;

  const total = Number(node.total);
  const target = Number(node.total_target);
  const pct = target > 0 ? Math.min(100, (total / target) * 100) : 0;
  const themed = theme !== "none";

  return (
    <section className="card overflow-hidden">
      <div className="bg-gradient-to-br from-ink-900 via-ink-900 to-brand-900 p-5 sm:p-6 md:p-7">
        <div className="flex items-start gap-4 mb-2">
          {theme === "boat" && (
            <div className="shrink-0 hidden sm:block">
              <Globe size={88} />
            </div>
          )}
          {theme === "haus" && (
            <div className="shrink-0 hidden sm:flex items-center justify-center text-brand-300">
              <HouseIcon size={88} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-base font-semibold tracking-tight text-ink-300">{node.name}</h2>
              <div className="flex items-center gap-2">
                {theme === "boat" && (
                  <span className="text-brand-300">
                    <Logo size={22} />
                  </span>
                )}
                {theme === "haus" && (
                  <span className="text-brand-300">
                    <HouseIcon size={22} />
                  </span>
                )}
                <span className="text-lg font-semibold num text-ink-100">
                  {pct.toFixed(1)} %
                </span>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-3xl sm:text-4xl md:text-5xl font-bold num text-ink-50">
                {fmtEUR(total)}
              </span>
              <span className="text-base text-ink-400">von {fmtEUR(target)}</span>
            </div>
            {monthlyContrib > 0 && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-sm">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-good-500" />
                <span className="text-ink-300">Beitrag diesen Monat:</span>
                <span className="num font-semibold text-good-500">
                  + {fmtEUR(monthlyContrib)}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className={themed ? "mt-5 relative h-9" : "mt-5 relative h-3"}>
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-3 rounded-full bg-ink-800 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-brand-500 to-brand-300 transition-all duration-700"
              style={{ width: `${pct}%` }}
            />
          </div>
          {themed && (
            <div
              className="absolute -translate-x-1/2 transition-all duration-700"
              style={{ left: `${pct}%`, top: -2 }}
              aria-hidden="true"
            >
              <span className="block text-brand-300 drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">
                {theme === "haus" ? <HouseIcon size={28} /> : <Logo size={28} />}
              </span>
            </div>
          )}
        </div>

        <div className="mt-2 flex justify-between text-[11px] text-ink-500 num">
          <span>0 €</span>
          <span>{fmtEUR(target)}</span>
        </div>
      </div>
    </section>
  );
}
